import type { FetchNextPageOptions, InfiniteQueryObserverResult } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import { useState, useMemo, useRef, useEffect } from "react";
import { useAllUsersInfinite } from "@components/TeamList/hooks/useUsers";
import { getFullNameOrEmail } from "@utils/tags.util";
import { usePersonOptions } from "./usePersonOptions";

interface QueryState {
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
}

function createEmptyResponse(hasNextPage: boolean = false): InfiniteQueryObserverResult<any[], Error> {
  return {
    data: [],
    error: undefined,
    isError: false,
    isFetched: true,
    isFetching: false,
    isLoading: false,
    isRefetching: false,
    refetch: async () => Promise.resolve({} as any),
    status: "success",
    fetchNextPage: async () => Promise.resolve({} as any),
    hasNextPage,
  } as unknown as InfiniteQueryObserverResult<any[], Error>;
}

function isFullyLoaded(query: QueryState): boolean {
  return !query.isLoading && !query.isFetchingNextPage && !query.hasNextPage;
}

export function usePersonAndUserOptions(companyId?: string | null) {
  const [searchQuery, setSearchQuery] = useState("");
  const isFetchingRef = useRef(false);
  const previousCompanyIdRef = useRef<string | null | undefined>(companyId);

  useEffect(() => {
    if (previousCompanyIdRef.current !== companyId) {
      setSearchQuery("");
      allPerson.setSearchQuery("");
      filteredPerson.setSearchQuery("");
      previousCompanyIdRef.current = companyId;
    }
  }, [companyId]);


  const customParams = useMemo(() => ({ initial: 20, limit: 20 }), []);

  const filteredPerson = usePersonOptions(customParams, companyId && !searchQuery ? companyId : null);
  const allPerson = usePersonOptions(customParams, null);
  const user = useAllUsersInfinite(searchQuery, false, customParams);
  const person = searchQuery ? allPerson : companyId ? filteredPerson : allPerson;

  const debouncedSearch = useMemo(() => {
    const debouncedFn = debounce((inputValue: string) => {
      setSearchQuery(inputValue);
      allPerson.setSearchQuery(inputValue);
      if (companyId) {
        filteredPerson.setSearchQuery(inputValue);
      }
    }, 300);
    return debouncedFn;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const groupedOptions = useMemo(() => {
    const deduplicateByValue = <T extends { value?: string; id?: string }>(items: T[]): T[] => {
      const seen = new Set<string>();
      return items.filter((item) => {
        const key = (item.value ?? item.id) as string;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    };

    const transformUser = (u: any, idx: number) => {
      const fullName = (u.full_name ?? "").trim();
      return {
        ...u,
        logo_url: u.avatar_url,
        key: u.id ?? idx,
        isUser: true,
        value: u.id,
        label: fullName || getFullNameOrEmail(u) || " ",
      };
    };

    const transformPerson = (p: any, idx: number) => ({
      ...p,
      key: p.value ?? p.id ?? idx,
      value: p.value ?? p.id ?? idx,
      label: getFullNameOrEmail(p) || " ",
      company_logo_url: p.primary_company?.company?.logo_url || null,
    });

    const users = (user.data || []).filter(Boolean).map(transformUser);
    const uniqueUsers = deduplicateByValue(users);

    if (companyId) {
      const allPersons = (allPerson.searchedOptions || []).filter(Boolean).map(transformPerson);
      const uniqueAllPersons = deduplicateByValue(allPersons);

      let filteredPersons;
      if (!searchQuery && filteredPerson.searchedOptions && filteredPerson.searchedOptions.length > 0) {
        filteredPersons = deduplicateByValue(filteredPerson.searchedOptions.filter(Boolean).map(transformPerson));
      } else {
        filteredPersons = uniqueAllPersons.filter((p) => p.primary_company?.company?.id === companyId);
      }

      const filteredPersonIds = new Set(filteredPersons.map((p) => p.value ?? p.id).filter(Boolean));
      const additionalPeople = uniqueAllPersons.filter((p) => !filteredPersonIds.has(p.value ?? p.id ?? ""));

      const groups = [];
      const peopleQuery = searchQuery ? allPerson : filteredPerson;

      // Show all sections when data is available - PEOPLE → TEAM MEMBERS → ADDITIONAL
      const showPeopleSection =
        filteredPersons.length > 0 ||
        peopleQuery.isLoading ||
        peopleQuery.isFetchingNextPage ||
        peopleQuery.hasNextPage;

      if (showPeopleSection) {
        groups.push({ label: "PEOPLE", options: filteredPersons });
      }

      const showTeamMembersSection =
        uniqueUsers.length > 0 || user.isLoading || user.isFetchingNextPage;

      if (showTeamMembersSection) {
        groups.push({ label: "TEAM MEMBERS", options: uniqueUsers });
      }

      const showAdditionalSection =
        !searchQuery &&
        (additionalPeople.length > 0 || allPerson.isLoading || allPerson.isFetchingNextPage);

      if (showAdditionalSection) {
        groups.push({ label: "ADDITIONAL", options: additionalPeople });
      }

      return groups;
    }

    const persons = (person.searchedOptions || []).filter(Boolean).map(transformPerson);
    const uniquePersons = deduplicateByValue(persons);
    const isTeamMembersFullyLoaded = isFullyLoaded(user);

    const groups = [];
    // Show TEAM MEMBERS first, then PEOPLE (after team members fully loaded)
    const showTeamMembers = uniqueUsers.length > 0 || user.isLoading || user.isFetchingNextPage;
    const showPeople =
      (uniquePersons.length > 0 || person.isLoading || person.isFetchingNextPage) &&
      (showTeamMembers ? isTeamMembersFullyLoaded : true);

    if (showTeamMembers) {
      groups.push({ label: "TEAM MEMBERS", options: uniqueUsers });
    }
    if (showPeople) {
      groups.push({ label: "PEOPLE", options: uniquePersons });
    }
    return groups;
  }, [
    user,
    companyId,
    person.searchedOptions,
    person.isLoading,
    person.isFetchingNextPage,
    person.hasNextPage,
    allPerson,
    searchQuery,
    filteredPerson,
  ]);

  const fetchNextPage = async (options?: FetchNextPageOptions) => {
    if (isFetchingRef.current) {
      return createEmptyResponse(false);
    }

    isFetchingRef.current = true;

    try {
      if (companyId) {
        const peopleQuery = searchQuery ? allPerson : filteredPerson;

        if (!searchQuery) {
          if (filteredPerson.hasNextPage && !filteredPerson.isFetchingNextPage) {
            const result = await filteredPerson.fetchNextPage(options);
            isFetchingRef.current = false;
            return result;
          }
        } else {
          if (allPerson.hasNextPage && !allPerson.isFetchingNextPage) {
            const result = await allPerson.fetchNextPage(options);
            isFetchingRef.current = false;
            return result;
          }
        }

        const isPeopleFullyLoaded = isFullyLoaded(peopleQuery);
        if (user.hasNextPage && !user.isFetchingNextPage && isPeopleFullyLoaded) {
          const result = await user.fetchNextPage(options);
          isFetchingRef.current = false;
          return result;
        }

        const isTeamMembersFullyLoaded = isFullyLoaded(user);
        if (allPerson.hasNextPage && !allPerson.isFetchingNextPage && isPeopleFullyLoaded && isTeamMembersFullyLoaded) {
          const result = await allPerson.fetchNextPage(options);
          isFetchingRef.current = false;
          return result;
        }
        isFetchingRef.current = false;
        return createEmptyResponse(false);
      } else {
        // No companyId: fetch users first, then people (matches display order: TEAM MEMBERS → PEOPLE)
        if (user.hasNextPage && !user.isFetchingNextPage) {
          const result = await user.fetchNextPage(options);
          isFetchingRef.current = false;
          return result;
        }

        if (person.hasNextPage && !person.isFetchingNextPage && isFullyLoaded(user)) {
          const result = await person.fetchNextPage(options);
          isFetchingRef.current = false;
          return result;
        }
        isFetchingRef.current = false;
        return createEmptyResponse(false);
      }
    } catch (error) {
      console.error("[usePersonAndUserOptions] fetchNextPage error:", error);
      isFetchingRef.current = false;
      return createEmptyResponse(false);
    }
  };

  const isLoading = companyId
    ? filteredPerson.isLoading || allPerson.isLoading || user.isLoading
    : person.isLoading || user.isLoading;

  const isFetchingNextPage = companyId
    ? filteredPerson.isFetchingNextPage || allPerson.isFetchingNextPage || user.isFetchingNextPage
    : person.isFetchingNextPage || user.isFetchingNextPage;

  const hasNextPage = (() => {
    if (companyId) {
      const peopleQuery = searchQuery ? allPerson : filteredPerson;

      if (peopleQuery.hasNextPage) {
        return true;
      }

      if (isFullyLoaded(peopleQuery) && user.hasNextPage) {
        return true;
      }

      if (!searchQuery && isFullyLoaded(peopleQuery) && isFullyLoaded(user) && allPerson.hasNextPage) {
        return true;
      }

      return false;
    } else {
      // No companyId: users first, then people (matches display order: TEAM MEMBERS → PEOPLE)
      if (user.hasNextPage) {
        return true;
      }

      if (isFullyLoaded(user) && person.hasNextPage) {
        return true;
      }

      return false;
    }
  })();

  return {
    groupedOptions,
    searchQuery,
    setSearchQuery,
    debouncedSearch,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    loadOptions: person.loadOptions,
  };
}
